import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import QRCode from 'qrcode';
import type {
  FeedbackReminderScheduleResponse,
  FeedbackReminderScheduleRow,
  FeedbackTrackerAuditResponse,
  FeedbackTrackerCommunicationHistoryResponse,
  FeedbackTrackerDetailResponse,
  FeedbackTrackerFeedbackResponse,
  FeedbackTrackerTimelineResponse,
  GeneralSettingsResponse,
} from '@ferty9/types';
import {
  addFeedbackTrackerNote,
  assignFeedbackTrackerCase,
  bulkFeedbackTrackerAction,
  createFeedbackRemoteLink,
  exportFeedbackTrackerCsv,
  getManualFeedbackSession,
  getFeedbackTrackerAudit,
  getFeedbackTrackerCommunicationHistory,
  getFeedbackTrackerDetail,
  getFeedbackTrackerFeedback,
  getFeedbackTracker,
  getFeedbackReminderSchedule,
  getFeedbackTrackerTimeline,
  scheduleFeedbackTrackerCallback,
  sendFeedbackReminder,
  sendFeedbackSms,
  sendManualFeedbackOtp,
  submitManualFeedback,
  verifyManualFeedbackOtp,
  updateFeedbackTrackerCaseStatus,
  type FeedbackRemoteLinkResponse,
  type ManualFeedbackSession,
  type ManualFeedbackSubmitPayload,
  type FeedbackTrackerBulkAction,
  type FeedbackTrackerBulkPayload,
  type FeedbackTrackerBulkResult,
  type FeedbackTrackerRow,
  type FeedbackTrackerStats,
} from '../lib/api/feedback-tracker';
import { resolveAnalyticalYearPeriod } from '../lib/api/analytical-periods';

const remoteLinkRequests = new Map<string, Promise<FeedbackRemoteLinkResponse>>();

function fmt(value: unknown) {
  return value == null || value === '' ? '-' : String(value);
}
function defaultCallbackAt() {
  const date = new Date(Date.now() + 60 * 60 * 1000);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}T${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}
function workflowActionTitle(request: TrackerWorkflowActionRequest) {
  if (request.kind === 'note') return 'Add Follow-up Note';
  if (request.kind === 'assign') return 'Transfer RM';
  if (request.kind === 'callback') return 'Schedule Callback';
  return String(request.value || 'Update Case').replaceAll('_', ' ').toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
}
function workflowActionDescription(request: TrackerWorkflowActionRequest) {
  if (request.kind === 'note') return 'Record an operational follow-up note for this visit.';
  if (request.kind === 'assign') return 'Assign or transfer this case to an RM or staff member.';
  if (request.kind === 'callback') return 'Schedule a callback and record the purpose for audit history.';
  return 'Update case status with a mandatory reason. This action is saved in the tracker audit trail.';
}
function defaultWorkflowNote(request: TrackerWorkflowActionRequest) {
  if (request.kind === 'note') return 'Follow-up reviewed by Relationship Manager.';
  if (request.kind === 'assign') return 'Case transferred for follow-up.';
  if (request.kind === 'callback') return 'Callback scheduled with patient.';
  return `${String(request.value || 'Case status').replaceAll('_', ' ')} recorded from tracker.`;
}
function requiredWorkflowMessage(request: TrackerWorkflowActionRequest) {
  if (request.kind === 'assign') return 'Enter RM or staff name.';
  if (request.kind === 'callback') return 'Enter callback date/time and note.';
  if (request.kind === 'case-status') return 'Enter reason for this case status.';
  return 'Enter follow-up note.';
}
function lc(value: unknown) {
  return String(value || '').toLowerCase();
}
function statusClass(value: unknown) {
  const v = lc(value);
  if (v.includes('fail') || v.includes('wrong') || v.includes('expired') || v.includes('risk') || v.includes('detractor')) return 'status-danger';
  if (v.includes('remote') || v.includes('taken') || v.includes('completed') || v.includes('tablet')) return 'status-good';
  if (v.includes('not_eligible') || v.includes('not eligible')) return 'status-muted';
  if (v.includes('sent') || v.includes('delivered') || v.includes('opened') || v.includes('automation')) return 'status-info';
  if (v.includes('pending') || v.includes('manual') || v.includes('callback')) return 'status-pending';
  return 'status-muted';
}
function senderLabel(value: unknown) {
  const sender = String(value || '').trim();
  if (!sender) return 'Unknown';
  return sender.toLowerCase() === 'system' ? 'Automatic' : `Manual (${sender})`;
}
function today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function dateInputValue(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}
function dateRangeFor(value: string) {
  const now = new Date();
  const start = new Date(now);
  const end = new Date(now);
  if (value === 'CURRENT_MONTH') {
    start.setDate(1);
    return { from: dateInputValue(start), to: dateInputValue(end) };
  }
  if (value === 'PREVIOUS_MONTH') {
    start.setMonth(now.getMonth() - 1, 1);
    end.setDate(0);
    return { from: dateInputValue(start), to: dateInputValue(end) };
  }
  if (value === 'LAST_3_MONTHS') {
    start.setMonth(now.getMonth() - 3);
    return { from: dateInputValue(start), to: dateInputValue(end) };
  }
  if (value === 'PREVIOUS_3_MONTHS') {
    start.setMonth(now.getMonth() - 3, 1);
    end.setMonth(now.getMonth(), 0);
    return { from: dateInputValue(start), to: dateInputValue(end) };
  }
  if (value === 'CURRENT_WEEK') {
    const day = now.getDay();
    start.setDate(now.getDate() - day);
    return { from: dateInputValue(start), to: dateInputValue(end) };
  }
  if (value === 'LAST_WEEK') {
    const day = now.getDay();
    start.setDate(now.getDate() - day - 7);
    end.setDate(now.getDate() - day - 1);
    return { from: dateInputValue(start), to: dateInputValue(end) };
  }
  if (/^\d+$/.test(value)) {
    start.setDate(now.getDate() - Number(value) + 1);
    return { from: dateInputValue(start), to: dateInputValue(end) };
  }
  return { from: '', to: '' };
}
function formatTimestamp(value: unknown) {
  const date = new Date(String(value || ''));
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}
function hours(minutes: unknown) {
  return Math.round(Number(minutes || 0) / 60);
}
function bucket(score: unknown) {
  if (score === null || score === undefined || score === '') return '-';
  const s = Number(score);
  if (!Number.isFinite(s)) return '-';
  return s >= 9 ? 'Promoter' : s >= 7 ? 'Passive' : 'Detractor';
}
function pct(num: number, den: number) {
  return den ? `${Number(((num / den) * 100).toFixed(1))}%` : '0%';
}
function hourOf(value: unknown) {
  const date = new Date(String(value || ''));
  return Number.isNaN(date.getTime()) ? null : date.getHours();
}
function isFeedbackTaken(row: FeedbackTrackerRow) {
  return ['TAKEN_AT_CLINIC', 'MANUAL_COMPLETED', 'REMOTE_COMPLETED'].includes(String(row.feedback_status));
}
function isPending(row: FeedbackTrackerRow) {
  return String(row.feedback_status) === 'PENDING';
}
function isTakenAtClinic(row: FeedbackTrackerRow) {
  const status = String(row.feedback_status);
  const mode = String(row.feedback_mode).toUpperCase();
  return status === 'TAKEN_AT_CLINIC' || status === 'TAKEN_ON_TABLET' || (status === 'Taken' && mode.includes('TABLET'));
}
function isStaffAcquired(row: FeedbackTrackerRow) {
  const status = String(row.feedback_status);
  const mode = String(row.feedback_mode).toUpperCase();
  return status === 'MANUAL_COMPLETED' || (mode.includes('MANUAL') && isFeedbackTaken(row));
}
function isResponsePending(row: FeedbackTrackerRow) {
  const messageStatus = String(row.message_status);
  return isPending(row) && (['SENT', 'DELIVERED', 'OPENED', 'EXPIRED'].includes(messageStatus) || String(row.feedback_status) === 'EXPIRED');
}
function isMessageTouched(row: FeedbackTrackerRow) {
  return ['MESSAGE_SENT'].includes(String(row.feedback_status)) || ['SENT', 'DELIVERED', 'OPENED'].includes(String(row.message_status));
}
function isRisk(row: FeedbackTrackerRow) {
  const note = lc(row.last_note);
  return ['FAILED', 'EXPIRED'].includes(String(row.message_status)) || bucket(row.nps_score) === 'Detractor' || (isPending(row) && Number(row.pending_since_minutes || 0) > 1440) || /complaint|refund|vip|legal|wrong|failed|escalat/.test(note);
}
function isManual(row: FeedbackTrackerRow) {
  return String(row.feedback_mode).includes('MANUAL') || (isPending(row) && Number(row.pending_since_minutes || 0) > 180) || lc(row.last_note).includes('callback');
}
function isAutomation(row: FeedbackTrackerRow) {
  return row.message_status !== 'NOT_SENT' || row.message_channel !== '-' || ['SENT', 'DELIVERED', 'OPENED', 'FAILED', 'EXPIRED'].includes(String(row.message_status));
}
function cleanOptions(values: Array<string | undefined> | undefined) {
  return [...new Set((values || []).map((value) => String(value || '').trim()).filter((value) => value && value.toUpperCase() !== 'ALL'))];
}
function safeNumber(value: unknown) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}
function normalizeStats(stats?: Partial<FeedbackTrackerStats>): FeedbackTrackerStats {
  return {
    totalVisits: safeNumber(stats?.totalVisits),
    eligibleVisits: safeNumber(stats?.eligibleVisits),
    eligibleMatrix: {
      newConsultation: safeNumber(stats?.eligibleMatrix?.newConsultation),
      opu: safeNumber(stats?.eligibleMatrix?.opu),
      et: safeNumber(stats?.eligibleMatrix?.et),
    },
    feedbackTaken: safeNumber(stats?.feedbackTaken),
    pending: safeNumber(stats?.pending),
    takenAtClinic: safeNumber(stats?.takenAtClinic),
    staffAcquired: safeNumber(stats?.staffAcquired),
    responsePending: safeNumber(stats?.responsePending),
    captureRate: safeNumber(stats?.captureRate),
    sent: safeNumber(stats?.sent),
    messageSent: safeNumber(stats?.messageSent),
    delivered: safeNumber(stats?.delivered),
    opened: safeNumber(stats?.opened),
    failed: safeNumber(stats?.failed),
    remoteCompleted: safeNumber(stats?.remoteCompleted),
    manualPending: safeNumber(stats?.manualPending),
    manualCompleted: safeNumber(stats?.manualCompleted),
    highPriority: safeNumber(stats?.highPriority),
    avgNps: safeNumber(stats?.avgNps),
    detractors: safeNumber(stats?.detractors),
    promoters: safeNumber(stats?.promoters),
    notMessaged: safeNumber(stats?.notMessaged),
    notResponded: safeNumber(stats?.notResponded),
  };
}

function feedbackTrackerManualProcessEnabled(settings?: GeneralSettingsResponse | null): boolean {
  const workflow = settings?.workflows?.find((item) => item.key === 'SURVEY_OPERATIONAL_CONTROLS');
  const setting = workflow?.settings.find((item) => item.setting_key === 'FEEDBACK_TRACKER_MANUAL_PROCESS_ENABLED');
  const value = setting?.setting_value_json;
  if (!value || typeof value !== 'object') return true;
  const raw = (value as { value?: unknown }).value;
  if (typeof raw === 'boolean') return raw;
  if (typeof raw === 'string') return raw.trim().toLowerCase() === 'true';
  return true;
}

type ViewTab =
  | 'ALL'
  | 'PENDING'
  | 'TAKEN_AT_CLINIC'
  | 'STAFF_ACQUIRED'
  | 'MESSAGE_SENT'
  | 'REMOTE_COMPLETED'
  | 'RESPONSE_PENDING'
  | 'MANUAL'
  | 'FAILED'
  | 'ANALYTICS'
  | 'HIGH_PRIORITY'
  | 'AUTOMATION'
  | 'HISTORY';

type TrackerPermissions = {
  manual: boolean;
  sendMessage: boolean;
  manage: boolean;
  assign: boolean;
  callback: boolean;
  escalate: boolean;
  export: boolean;
};

type TrackerRowActionKind = 'manual' | 'sms' | 'note' | 'assign' | 'callback' | 'case-status';
type TrackerWorkflowActionRequest = {
  kind: Exclude<TrackerRowActionKind, 'manual' | 'sms'>;
  row: FeedbackTrackerRow;
  value?: string;
};

const defaultTrackerPermissions: TrackerPermissions = {
  manual: false,
  sendMessage: false,
  manage: false,
  assign: false,
  callback: false,
  escalate: false,
  export: false,
};

const viewTabs: Array<{ key: ViewTab; label: string; hint: string }> = [
  { key: 'ALL', label: 'All Visits', hint: 'Complete visit-wise master list' },
  { key: 'PENDING', label: 'Pending', hint: 'Feedback not yet captured' },
  { key: 'TAKEN_AT_CLINIC', label: 'Taken at Clinic', hint: 'Feedback completed before the patient left clinic' },
  { key: 'STAFF_ACQUIRED', label: 'Staff Acquired', hint: 'Feedback captured manually by RM/staff' },
  { key: 'MESSAGE_SENT', label: 'Message Sent', hint: 'WhatsApp/SMS sent and waiting' },
  { key: 'REMOTE_COMPLETED', label: 'Remote Completed', hint: 'Completed from patient mobile' },
  { key: 'RESPONSE_PENDING', label: 'Response Pending', hint: 'Message delivered/opened but feedback not completed' },
  { key: 'MANUAL', label: 'Manual / Callback', hint: 'RM call and next-visit capture' },
  { key: 'FAILED', label: 'Failed / Risk', hint: 'Failures, detractors and escalations' },
  { key: 'ANALYTICS', label: 'Analytics', hint: 'Trends, funnel and distribution' },
  { key: 'HIGH_PRIORITY', label: 'High Priority', hint: 'VIP, detractor and aged cases' },
  { key: 'AUTOMATION', label: 'Automation', hint: 'System reminders and message lifecycle' },
  { key: 'HISTORY', label: 'History', hint: 'Completed and actioned feedback timeline' },
];

const emptyFeedbackTrackerRows: FeedbackTrackerRow[] = [];

export function FeedbackTrackerTab({ active, token, centers, generalSettings, permissions = defaultTrackerPermissions, notify, onTopBarActionsChange }: { active: boolean; token?: string; centers: string[]; generalSettings?: GeneralSettingsResponse | null; permissions?: TrackerPermissions; notify: (message: string, type?: 'success' | 'error' | 'warning' | 'info') => void; onTopBarActionsChange?: (actions: ReactNode | null) => void }) {
  const [dateRange, setDateRange] = useState('CURRENT_MONTH');
  const [dateFrom, setDateFrom] = useState(() => dateRangeFor('CURRENT_MONTH').from);
  const [dateTo, setDateTo] = useState(() => dateRangeFor('CURRENT_MONTH').to);
  const [center, setCenter] = useState('ALL');
  const [doctor, setDoctor] = useState('ALL');
  const [visitType, setVisitType] = useState('ALL');
  const [journey, setJourney] = useState('ALL');
  const [status, setStatus] = useState('ALL');
  const [feedbackMode, setFeedbackMode] = useState('ALL');
  const [messageStatus, setMessageStatus] = useState('ALL');
  const [npsBucket, setNpsBucket] = useState('ALL');
  const [appointmentId, setAppointmentId] = useState('');
  const [priority, setPriority] = useState('ALL');
  const [caseStatus, setCaseStatus] = useState('ALL');
  const [callbackStatus, setCallbackStatus] = useState('ALL');
  const [search, setSearch] = useState('');
  const [mobile, setMobile] = useState('');
  const [assignedTo, setAssignedTo] = useState('ALL');
  const [view, setView] = useState<ViewTab>('ALL');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Awaited<ReturnType<typeof getFeedbackTracker>> | null>(null);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [selectedRow, setSelectedRow] = useState<FeedbackTrackerRow | null>(null);
  const [messageRow, setMessageRow] = useState<FeedbackTrackerRow | null>(null);
  const [manualRow, setManualRow] = useState<FeedbackTrackerRow | null>(null);
  const [workflowAction, setWorkflowAction] = useState<TrackerWorkflowActionRequest | null>(null);
  const [remoteLinkRequest, setRemoteLinkRequest] = useState<{ row: FeedbackTrackerRow; purpose: 'COPY' | 'QR' } | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await getFeedbackTracker(token, {
        dateFrom,
        dateTo,
        center,
        doctor,
        visitType,
        journey,
        status,
        feedbackMode,
        messageStatus,
        npsBucket,
        appointmentId,
        priority,
        caseStatus,
        callbackStatus,
        search,
        mobile,
        assignedTo,
      }));
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Unable to load Feedback Tracker records.');
    } finally {
      setLoading(false);
    }
  }, [appointmentId, assignedTo, callbackStatus, caseStatus, center, dateFrom, dateTo, doctor, feedbackMode, journey, messageStatus, mobile, notify, npsBucket, priority, search, status, token, visitType]);

  const updateDateRange = async (value: string) => {
    setDateRange(value);
    if (value === 'CUSTOM') {
      const currentMonth = dateRangeFor('CURRENT_MONTH');
      setDateFrom((current) => current || currentMonth.from);
      setDateTo((current) => current || currentMonth.to);
      return;
    }
    if (value === 'YTD' || value === 'FY_YTD') {
      try {
        const range = await resolveAnalyticalYearPeriod(value, token);
        setDateFrom(range.from);
        setDateTo(range.to);
      } catch (error) {
        notify(error instanceof Error ? error.message : 'Unable to resolve the selected reporting period.');
      }
      return;
    }
    const range = dateRangeFor(value);
    setDateFrom(range.from || today());
    setDateTo(range.to || today());
  };

  useEffect(() => {
    if (active && !data) void load();
  }, [active, data, load]);

  const rows = data?.rows ?? emptyFeedbackTrackerRows;
  const visibleRows = useMemo(() => rows.filter((row) => {
    if (view === 'ALL' || view === 'ANALYTICS') return true;
    if (view === 'PENDING') return isPending(row);
    if (view === 'TAKEN_AT_CLINIC') return isTakenAtClinic(row);
    if (view === 'STAFF_ACQUIRED') return isStaffAcquired(row);
    if (view === 'MESSAGE_SENT') return isMessageTouched(row);
    if (view === 'REMOTE_COMPLETED') return String(row.feedback_status) === 'REMOTE_COMPLETED';
    if (view === 'RESPONSE_PENDING') return isResponsePending(row);
    if (view === 'MANUAL') return isManual(row);
    if (view === 'FAILED') return isRisk(row);
    if (view === 'HIGH_PRIORITY') return isRisk(row) || Number(row.nps_score) <= 6 || (isPending(row) && Number(row.pending_since_minutes || 0) > 720);
    if (view === 'AUTOMATION') return isAutomation(row);
    if (view === 'HISTORY') return isFeedbackTaken(row) || Boolean(row.last_note) || isMessageTouched(row);
    return true;
  }), [rows, view]);
  const visibleIds = useMemo(() => visibleRows.map((row) => row.appointment_id), [visibleRows]);
  const selectedVisibleCount = selectedIds.filter((id) => visibleIds.includes(id)).length;
  const allVisibleSelected = visibleIds.length > 0 && selectedVisibleCount === visibleIds.length;

  const stats = useMemo(() => normalizeStats(data?.stats), [data?.stats]);
  const manualProcessEnabled = useMemo(() => feedbackTrackerManualProcessEnabled(generalSettings), [generalSettings]);

  useEffect(() => {
    const rowIds = new Set(rows.map((row) => row.appointment_id));
    setSelectedIds((current) => {
      const next = current.filter((id) => rowIds.has(id));
      if (next.length === current.length && next.every((id, index) => id === current[index])) {
        return current;
      }
      return next;
    });
  }, [rows]);

  const filterCenters = useMemo(() => cleanOptions([...(centers || []), ...(data?.filterOptions?.centers || [])]), [centers, data]);
  const doctors = cleanOptions(data?.filterOptions?.doctors);
  const staff = cleanOptions(data?.filterOptions?.assignedTo);
  const visitTypes = cleanOptions(data?.filterOptions?.visitTypes);
  const journeys = cleanOptions(data?.filterOptions?.journeys);
  const feedbackStatuses = cleanOptions(data?.filterOptions?.feedbackStatuses);
  const feedbackModes = cleanOptions(data?.filterOptions?.feedbackModes);
  const messageStatuses = cleanOptions(data?.filterOptions?.messageStatuses);
  const priorities = cleanOptions(data?.filterOptions?.priorities);
  const caseStatuses = cleanOptions(data?.filterOptions?.caseStatuses);
  const callbackStatuses = cleanOptions(data?.filterOptions?.callbackStatuses);

  const action = async (kind: TrackerRowActionKind, row: FeedbackTrackerRow, value = 'WHATSAPP') => {
    try {
      if (kind === 'manual') {
        if (!manualProcessEnabled) {
          notify('Manual process is disabled. Please contact your company administrator.', 'warning');
          setOpenMenu(null);
          return;
        }
        setManualRow(row);
        return;
      }
      if (kind === 'sms') await sendFeedbackSms(token, row.appointment_id);
      if (kind === 'note' || kind === 'assign' || kind === 'callback' || kind === 'case-status') {
        setWorkflowAction({ kind, row, value });
        setOpenMenu(null);
        return;
      }
      setOpenMenu(null);
      notify(kind === 'sms' ? 'SMS feedback link sent.' : 'Tracker action recorded.');
      await load();
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Tracker action failed.');
    }
  };

  const submitWorkflowAction = async (payload: {
    kind: TrackerWorkflowActionRequest['kind'];
    row: FeedbackTrackerRow;
    value?: string;
    note?: string;
    assignedDisplayName?: string;
    callbackAt?: string;
    caseStatus?: string;
    priority?: string;
    reason?: string;
  }) => {
    if (payload.kind === 'note') {
      await addFeedbackTrackerNote(token, payload.row.appointment_id, payload.note || 'Follow-up reviewed by Relationship Manager.');
    }
    if (payload.kind === 'assign') {
      await assignFeedbackTrackerCase(
        token,
        payload.row.appointment_id,
        payload.assignedDisplayName || '',
        payload.note || `Assigned to ${payload.assignedDisplayName}.`,
      );
    }
    if (payload.kind === 'callback') {
      await scheduleFeedbackTrackerCallback(
        token,
        payload.row.appointment_id,
        payload.callbackAt || '',
        payload.note || 'Callback scheduled with patient.',
      );
    }
    if (payload.kind === 'case-status') {
      const caseStatusValue = payload.caseStatus || payload.value || 'ACTIVE';
      await updateFeedbackTrackerCaseStatus(
        token,
        payload.row.appointment_id,
        caseStatusValue,
        payload.reason || `${caseStatusValue.replaceAll('_', ' ')} recorded from tracker.`,
        payload.priority || (caseStatusValue === 'ESCALATED' || caseStatusValue === 'WRONG_NUMBER' ? 'HIGH' : 'NORMAL'),
      );
    }
    setWorkflowAction(null);
    notify('Tracker action recorded.');
    await load();
  };

  const trackerParams = useCallback((): Record<string, string> => ({
    dateFrom,
    dateTo,
    center,
    doctor,
    visitType,
    journey,
    status,
    feedbackMode,
    messageStatus,
    npsBucket,
    appointmentId,
    priority,
    caseStatus,
    callbackStatus,
    search,
    mobile,
    assignedTo,
  }), [appointmentId, assignedTo, callbackStatus, caseStatus, center, dateFrom, dateTo, doctor, feedbackMode, journey, messageStatus, mobile, npsBucket, priority, search, status, visitType]);

  const toggleSelected = (appointmentIdValue: string) => {
    setSelectedIds((current) => current.includes(appointmentIdValue) ? current.filter((id) => id !== appointmentIdValue) : [...current, appointmentIdValue]);
  };

  const toggleVisibleSelected = () => {
    setSelectedIds((current) => {
      const currentSet = new Set(current);
      if (allVisibleSelected) {
        visibleIds.forEach((id) => currentSet.delete(id));
      } else {
        visibleIds.forEach((id) => currentSet.add(id));
      }
      return [...currentSet];
    });
  };

  const exportRows = useCallback(async (ids?: string[]) => {
    if (!permissions.export) {
      notify('You do not have permission to export Feedback Tracker rows.');
      return;
    }
    const exportIds = ids?.length ? ids : selectedIds.length ? selectedIds : [];
    try {
      const params = exportIds.length ? { ...trackerParams(), ids: exportIds.join(',') } : trackerParams();
      const { filename, content } = await exportFeedbackTrackerCsv(token, params);
      const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      notify(`Exported ${exportIds.length || 'filtered'} Feedback Tracker rows.`);
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Feedback Tracker export failed.');
    }
  }, [notify, permissions.export, selectedIds, token, trackerParams]);

  const runBulk = useCallback(async (payload: FeedbackTrackerBulkPayload): Promise<FeedbackTrackerBulkResult> => {
    const result = await bulkFeedbackTrackerAction(token, payload);
    notify(`Bulk action completed: ${result.succeeded} success, ${result.failed} failed.`);
    await load();
    return result;
  }, [load, notify, token]);

  useEffect(() => {
    if (!onTopBarActionsChange) return undefined;
    if (!active) return undefined;

    onTopBarActionsChange(
      <>
        <button className="btn btn-outline compact-btn" onClick={() => void load()} disabled={loading}>Refresh</button>
        <button className="btn btn-outline compact-btn" onClick={() => setBulkOpen(true)} disabled={!selectedIds.length}>Bulk Actions</button>
        <button className="btn btn-primary compact-btn" onClick={() => void exportRows()}>Export</button>
      </>,
    );

    return () => onTopBarActionsChange(null);
  }, [active, exportRows, load, loading, onTopBarActionsChange, selectedIds.length]);

  const resetFilters = () => {
    setSearch('');
    setMobile('');
    setAppointmentId('');
    setDateRange('CURRENT_MONTH');
    setDateFrom(dateRangeFor('CURRENT_MONTH').from);
    setDateTo(dateRangeFor('CURRENT_MONTH').to);
    setCenter('ALL');
    setDoctor('ALL');
    setVisitType('ALL');
    setJourney('ALL');
    setStatus('ALL');
    setFeedbackMode('ALL');
    setMessageStatus('ALL');
    setNpsBucket('ALL');
    setPriority('ALL');
    setCaseStatus('ALL');
    setCallbackStatus('ALL');
    setAssignedTo('ALL');
    setView('ALL');
    setSelectedIds([]);
  };

  const selectedTab = viewTabs.find((item) => item.key === view) || viewTabs[0];

  return (
    <section className="tab-content enterprise-compact enterprise-page" style={{ display: active ? 'block' : 'none' }}>
      <div className="ops-kpi-grid tracker-kpi-grid rich-tracker-kpi-grid">
        <Kpi label="Total Visits" value={stats.totalVisits} tone="blue" icon="👥" />
        <Kpi label="Eligible Visits" value={stats.eligibleVisits} tone="indigo" icon="✓" />
        <Kpi label="Eligible Matrix" value={<EligibleMatrix matrix={stats.eligibleMatrix} />} tone="slate" icon="☷" />
        <Kpi label="Feedback Taken" value={stats.feedbackTaken} tone="green" icon="✅" />
        <Kpi label="Taken at Clinic" value={stats.takenAtClinic} tone="green" icon="🏥" />
        <Kpi label="Staff Acquired" value={stats.staffAcquired} tone="indigo" icon="🧑‍💼" />
        <Kpi label="Pending" value={stats.pending} tone="amber" icon="⏳" />
        <Kpi label="Capture Rate" value={stats.captureRate} tone="pink" icon="📈" />
        <Kpi label="Message Sent" value={stats.sent} tone="indigo" icon="💬" />
        <Kpi label="Delivered / Opened" value={`${stats.delivered}/${stats.opened}`} tone="green" icon="📨" />
        <Kpi label="Manual / Callback" value={stats.manualPending} tone="red" icon="📞" />
        <Kpi label="High Priority" value={stats.highPriority} tone="red" icon="🚨" />
        <Kpi label="Remote Done" value={stats.remoteCompleted} tone="green" icon="📱" />
        <Kpi label="Avg NPS" value={stats.avgNps} tone="slate" icon="⭐" />
      </div>

      <div className="tracker-insight-strip">
        <Insight label="Suggested Action" value={`${stats.pending} eligible visits need follow-up today`} />
        <Insight label="Message Opportunity" value={`${stats.notMessaged} patients not yet messaged`} />
        <Insight label="High Priority" value={`${stats.highPriority} detractor / aged / failed cases`} />
        <Insight label="Last Updated" value={formatTimestamp(data?.lastUpdated || data?.generatedAt)} />
        <button className="mini-btn pink" onClick={() => { setView('PENDING'); setStatus('PENDING'); }}>View pending</button>
        <button className="mini-btn green" onClick={() => { setSelectedIds(rows.filter(isPending).map((row) => row.appointment_id)); setBulkOpen(true); }}>Send WhatsApp to pending</button>
      </div>

      <div className="card enterprise-filter-card">
        <div className="enterprise-filter-grid two-line-filter-grid tracker-filter-grid">
          <Field label="Date Range"><select value={dateRange} onChange={(event) => void updateDateRange(event.target.value)}><option value="CURRENT_MONTH">Current Month</option><option value="PREVIOUS_MONTH">Previous Month</option><option value="LAST_3_MONTHS">Last 3 Months</option><option value="PREVIOUS_3_MONTHS">Previous 3 Months</option><option value="CURRENT_WEEK">Current Week</option><option value="LAST_WEEK">Last Week</option><option value="YTD">YTD (Year to Date)</option><option value="FY_YTD">FY-YTD (Financial Year to Date)</option><option value="CUSTOM">Custom Date</option><option value="7">Last 7 Days</option><option value="30">Last 30 Days</option><option value="90">Last 90 Days</option><option value="365">Last Year</option></select></Field>
          {dateRange === 'CUSTOM' && <Field label="Custom Dates"><div className="tracker-range-field"><input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} /><input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} /></div></Field>}
          <Field label="Center"><select value={center} onChange={(event) => setCenter(event.target.value)}><option value="ALL">All Centers</option>{filterCenters.map((c, i) => <option key={`center-${c}-${i}`} value={c}>{c}</option>)}</select></Field>
          <Field label="Patient Type"><select value={doctor} onChange={(event) => setDoctor(event.target.value)}><option value="ALL">All Patient Types</option>{doctors.map((d, i) => <option key={`doctor-${d}-${i}`} value={d}>{d}</option>)}</select></Field>
          <Field label="Visit Type"><select value={visitType} onChange={(event) => setVisitType(event.target.value)}><option value="ALL">All Types</option>{visitTypes.map((value, i) => <option key={`visit-${value}-${i}`} value={value}>{value}</option>)}</select></Field>
          <Field label="Journey"><select value={journey} onChange={(event) => setJourney(event.target.value)}><option value="ALL">All Journeys</option>{journeys.map((value, i) => <option key={`journey-${value}-${i}`} value={value}>{value}</option>)}</select></Field>
          <Field label="Feedback Status"><select value={status} onChange={(event) => setStatus(event.target.value)}><option value="ALL">All Status</option>{feedbackStatuses.map((value, i) => <option key={`feedback-status-${value}-${i}`} value={value}>{value.replaceAll('_', ' ')}</option>)}</select></Field>
          <Field label="Feedback Mode"><select value={feedbackMode} onChange={(event) => setFeedbackMode(event.target.value)}><option value="ALL">All Modes</option>{feedbackModes.map((value, i) => <option key={`feedback-mode-${value}-${i}`} value={value}>{value.replaceAll('_', ' ')}</option>)}</select></Field>
          <Field label="Message Status"><select value={messageStatus} onChange={(event) => setMessageStatus(event.target.value)}><option value="ALL">All Status</option>{messageStatuses.map((value, i) => <option key={`message-status-${value}-${i}`} value={value}>{value.replaceAll('_', ' ')}</option>)}</select></Field>
          <Field label="Search Patient / MRN"><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Name, MRN or appointment..." /></Field>
          <Field label="Mobile Number"><input value={mobile} onChange={(event) => setMobile(event.target.value)} placeholder="Enter mobile number" /></Field>
          <Field label="Appointment ID"><input value={appointmentId} onChange={(event) => setAppointmentId(event.target.value)} placeholder="Appointment ID" /></Field>
          <Field label="Assigned To"><select value={assignedTo} onChange={(event) => setAssignedTo(event.target.value)}><option value="ALL">All RM / Staff</option>{staff.map((s, i) => <option key={`staff-${s}-${i}`} value={s}>{s}</option>)}</select></Field>
          <Field label="NPS Bucket"><select value={npsBucket} onChange={(event) => setNpsBucket(event.target.value)}><option value="ALL">All Buckets</option><option value="Promoter">Promoter</option><option value="Passive">Passive</option><option value="Detractor">Detractor</option></select></Field>
          <Field label="Priority"><select value={priority} onChange={(event) => setPriority(event.target.value)}><option value="ALL">All Priorities</option>{priorities.map((value, i) => <option key={`priority-${value}-${i}`} value={value}>{value.replaceAll('_', ' ')}</option>)}</select></Field>
          <Field label="Case Status"><select value={caseStatus} onChange={(event) => setCaseStatus(event.target.value)}><option value="ALL">All Case Statuses</option>{caseStatuses.map((value, i) => <option key={`case-${value}-${i}`} value={value}>{value.replaceAll('_', ' ')}</option>)}</select></Field>
          <Field label="Callback Status"><select value={callbackStatus} onChange={(event) => setCallbackStatus(event.target.value)}><option value="ALL">All Callback Statuses</option>{callbackStatuses.map((value, i) => <option key={`callback-${value}-${i}`} value={value}>{value.replaceAll('_', ' ')}</option>)}</select></Field>
          <button className="btn btn-outline compact-btn filter-action" onClick={resetFilters}>Clear Filters</button>
          <button className="btn btn-primary compact-btn filter-action" onClick={() => void load()}>{loading ? 'Loading...' : 'Apply Filters'}</button>
        </div>
      </div>

      <div className="operations-tabs tracker-tabs enhanced-tracker-tabs">{viewTabs.map((item) => <button key={item.key} className={view === item.key ? 'active' : ''} title={item.hint} onClick={() => setView(item.key)}><strong>{item.label}</strong><span>{item.hint}</span></button>)}</div>

      <WorkspaceHeader view={view} stats={stats} rows={visibleRows} selectedTab={selectedTab} onExport={() => void exportRows(visibleIds)} onBulk={() => setBulkOpen(true)} />

      {view === 'ANALYTICS' ? <TrackerAnalytics rows={rows} /> : (
        <div className="card enterprise-table-card">
          <div className="enterprise-card-heading"><div><strong>{selectedTab.label} Workspace</strong><span>{visibleRows.length} matching visits / {selectedTab.hint}</span></div><span className="live-pill">Live Live tracker</span></div>
          <div className="enterprise-table-wrap">
            <table className="enterprise-table">
              <thead><tr><th><input type="checkbox" aria-label="Select visible rows" checked={allVisibleSelected} onChange={toggleVisibleSelected} /></th><th>Visit</th><th>Patient</th><th>Patient Type / Journey</th><th>Checkout Status</th><th>Feedback</th><th>Mode</th><th>Message</th><th>Pending</th><th>Actions</th></tr></thead>
              <tbody>{visibleRows.length ? visibleRows.map((row) => <tr key={row.id}>
                <td><input type="checkbox" aria-label={`Select ${fmt(row.patient_name)}`} checked={selectedIds.includes(row.appointment_id)} onChange={() => toggleSelected(row.appointment_id)} /></td>
                <td><strong>{fmt(row.appointment_date).slice(0, 10)}</strong><small>{fmt(row.appointment_type)} / {fmt(row.appointment_status)}</small></td>
                <td><button className="link-cell" onClick={() => setSelectedRow(row)}><strong>{fmt(row.patient_name)}</strong><small>{fmt(row.mrn)} / {fmt(row.phone)}</small></button></td>
                <td><strong>{fmt(row.doctor_name)}</strong><small>{fmt(row.center)} / {fmt(row.journey)}</small></td>
                <td><span className={`status-chip ${statusClass(row.checkout_status || row.appointment_status)}`}>{fmt(row.checkout_status || row.appointment_status).replaceAll('_', ' ')}</span><small>{fmt(row.appointment_status)}</small></td>
                <td><span className={`status-chip ${statusClass(row.feedback_status)}`}>{fmt(row.feedback_status).replaceAll('_', ' ')}</span>{row.nps_score != null && <small>NPS {row.nps_score} / {fmt(row.nps_bucket || bucket(row.nps_score))}</small>}</td>
                <td>{fmt(row.feedback_mode)}</td>
                <td><span className={`status-chip ${statusClass(row.message_status)}`}>{fmt(row.message_status).replaceAll('_', ' ')}</span><small>{fmt(row.message_channel)}</small></td>
                <td><strong>{hours(row.pending_since_minutes)}h</strong><small>{fmt(row.last_note)}</small></td>
                <td><RowActionsV2 row={row} permissions={permissions} openMenu={openMenu} setOpenMenu={setOpenMenu} setSelectedRow={setSelectedRow} openMessageSchedule={setMessageRow} openRemoteLink={(purpose) => setRemoteLinkRequest({ row, purpose })} action={action} /></td>
              </tr>) : <tr><td colSpan={10} className="empty-cell">No visit feedback records found for selected filters.</td></tr>}</tbody>
            </table>
          </div>
        </div>
      )}
      {selectedRow && <DetailsDrawer row={selectedRow} token={token} onClose={() => setSelectedRow(null)} notify={notify} />}
      {workflowAction && <TrackerWorkflowActionModal request={workflowAction} onClose={() => setWorkflowAction(null)} onSubmit={submitWorkflowAction} notify={notify} />}
      {manualRow && <ManualFeedbackModal row={manualRow} token={token} onClose={() => setManualRow(null)} onCompleted={async () => {
        setManualRow(null);
        notify('Manual feedback completed.');
        await load();
      }} notify={notify} />}
      {messageRow && <ReminderScheduleModal row={messageRow} token={token} onClose={() => setMessageRow(null)} onSend={async (attempt) => {
        await sendFeedbackReminder(token, messageRow.appointment_id, attempt);
        notify(`Reminder ${attempt} sent.`);
        await load();
      }} notify={notify} />}
      {remoteLinkRequest && <RemoteLinkModal row={remoteLinkRequest.row} purpose={remoteLinkRequest.purpose} token={token} onClose={() => setRemoteLinkRequest(null)} notify={notify} />}
      {bulkOpen && <BulkActionModal selectedIds={selectedIds} permissions={permissions} onClose={() => setBulkOpen(false)} onSubmit={runBulk} onExport={() => void exportRows(selectedIds)} />}
    </section>
  );
}

function WorkspaceHeader({ view, stats, rows, selectedTab, onExport, onBulk }: { view: ViewTab; stats: FeedbackTrackerStats; rows: FeedbackTrackerRow[]; selectedTab: { label: string; hint: string }; onExport: () => void; onBulk: () => void }) {
  const cards: Record<ViewTab, Array<[string, string | number, string]>> = {
    ALL: [['Workspace', selectedTab.label, 'Complete tracker'], ['Rows', rows.length, 'Matching visits'], ['Action', 'Review', 'Use filters to narrow records']],
    PENDING: [['Pending Cases', rows.length, 'Eligible not completed'], ['Not Messaged', Number(stats.notMessaged || 0), 'Send link'], ['SLA Focus', `${rows.filter((r) => isPending(r) && Number(r.pending_since_minutes || 0) > 180).length}`, 'Older than 3h']],
    TAKEN_AT_CLINIC: [['Taken at Clinic', rows.length, 'In-clinic tablet capture'], ['Clinic Success', Number(stats.takenAtClinic || 0), 'Captured before exit'], ['Quality', 'Immediate', 'Best response freshness']],
    STAFF_ACQUIRED: [['Staff Acquired', rows.length, 'RM/staff manual capture'], ['Manual Done', Number(stats.staffAcquired || 0), 'Completed by staff'], ['Follow-up', 'Closed', 'Callback/next visit']],
    MESSAGE_SENT: [['Message Sent', rows.length, 'WhatsApp/SMS lifecycle'], ['Delivered', rows.filter((r) => r.message_status === 'DELIVERED').length, 'Provider callback'], ['Opened', rows.filter((r) => r.message_status === 'OPENED').length, 'Link opened']],
    REMOTE_COMPLETED: [['Remote Done', rows.length, 'Mobile completion'], ['Completion Share', pct(rows.length, Number(stats.eligibleVisits || 0)), 'Selected rows'], ['Avg NPS', Number(stats.avgNps || 0), 'Remote score average']],
    RESPONSE_PENDING: [['Response Pending', rows.length, 'Message sent but feedback open'], ['Delivered/Open', rows.filter((r) => ['DELIVERED','OPENED','SENT'].includes(String(r.message_status))).length, 'Awaiting completion'], ['Escalate', rows.filter((r) => Number(r.pending_since_minutes || 0) > 720).length, 'Older than 12h']],
    MANUAL: [['Manual Queue', rows.length, 'RM call required'], ['Callbacks Due', rows.filter((r) => isPending(r) && Number(r.pending_since_minutes || 0) > 180).length, 'Older than 3h'], ['Completed Manual', Number(stats.manualCompleted || 0), 'Recorded by RM']],
    FAILED: [['Risk Cases', rows.length, 'Need attention'], ['Message Failed', Number(stats.failed || 0), 'Retry needed'], ['Detractors', Number(stats.detractors || 0), 'NPS <= 6']],
    ANALYTICS: [['Analytics', 'Live', 'Graphs and funnels'], ['Capture Rate', stats.captureRate, 'Selected date'], ['Avg NPS', stats.avgNps, 'Scored responses']],
    HIGH_PRIORITY: [['High Priority', rows.length, 'VIP / aged / detractor'], ['Aged >12h', rows.filter((r) => isPending(r) && Number(r.pending_since_minutes || 0) > 720).length, 'Escalate'], ['Deterioration', Number(stats.detractors || 0), 'Low NPS']],
    AUTOMATION: [['Automation Events', rows.length, 'Message/reminder lifecycle'], ['Sent', Number(stats.sent || 0), 'System action'], ['Failed', Number(stats.failed || 0), 'Provider/technical issue']],
    HISTORY: [['History Records', rows.length, 'Completed/actioned'], ['Feedback Taken', Number(stats.feedbackTaken || 0), 'Closed loop'], ['Remote Complete', Number(stats.remoteCompleted || 0), 'Mobile submitted']],
  };
  return <div className="tracker-workspace-summary">
    <div className="tracker-workspace-title"><strong>{selectedTab.label}</strong><span>{selectedTab.hint}</span></div>
    <div className="tracker-workspace-cards">{cards[view].map(([label, value, note]) => <div key={label}><span>{label}</span><strong>{value}</strong><em>{note}</em></div>)}</div>
    <div className="tracker-workspace-actions"><button className="mini-btn" onClick={onExport}>Export Tab</button><button className="mini-btn pink" onClick={onBulk}>Bulk Workflow</button></div>
  </div>;
}

function BulkActionModal({ selectedIds, permissions, onClose, onSubmit, onExport }: { selectedIds: string[]; permissions: TrackerPermissions; onClose: () => void; onSubmit: (payload: FeedbackTrackerBulkPayload) => Promise<FeedbackTrackerBulkResult>; onExport: () => void }) {
  const [action, setAction] = useState<FeedbackTrackerBulkAction>('SEND_REMINDER');
  const [attempt, setAttempt] = useState(1);
  const [channel, setChannel] = useState<'WHATSAPP' | 'SMS' | 'BOTH'>('WHATSAPP');
  const [assignedDisplayName, setAssignedDisplayName] = useState('');
  const [caseStatus, setCaseStatus] = useState('EXEMPT');
  const [priority, setPriority] = useState('NORMAL');
  const [callbackAt, setCallbackAt] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<FeedbackTrackerBulkResult | null>(null);
  const permissionAllowed =
    action === 'SEND_REMINDER' ? permissions.sendMessage :
      action === 'ASSIGN' ? permissions.assign :
        action === 'CALLBACK' ? permissions.callback :
          permissions.escalate;
  const selectedCount = selectedIds.length;
  const canSubmit = selectedCount > 0 && permissionAllowed && (action !== 'ASSIGN' || assignedDisplayName.trim()) && (action !== 'CALLBACK' || callbackAt.trim());

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload: FeedbackTrackerBulkPayload = {
        action,
        appointmentIds: selectedIds,
        attempt,
        channel,
        assignedDisplayName,
        assignedUsername: assignedDisplayName,
        caseStatus,
        priority,
        callbackAt,
        note,
        reason: note,
      };
      setResult(await onSubmit(payload));
    } finally {
      setSubmitting(false);
    }
  };

  return <div className="enterprise-drawer-backdrop reminder-modal-backdrop" onClick={onClose}>
    <aside className="enterprise-drawer feedback-bulk-modal" onClick={(event) => event.stopPropagation()}>
      <button className="drawer-close" onClick={onClose}>x</button>
      <p className="enterprise-eyebrow">Feedback tracker bulk workflow</p>
      <h3>Bulk Actions</h3>
      <p>{selectedCount} selected visit{selectedCount === 1 ? '' : 's'}. Batch limit is 100 rows.</p>
      <div className="bulk-action-grid">
        <Field label="Action"><select value={action} onChange={(event) => setAction(event.target.value as FeedbackTrackerBulkAction)}><option value="SEND_REMINDER">Send reminder</option><option value="ASSIGN">Assign RM</option><option value="CASE_STATUS">Mark case status</option><option value="CALLBACK">Schedule callback</option></select></Field>
        {action === 'SEND_REMINDER' && <><Field label="Reminder"><select value={attempt} onChange={(event) => setAttempt(Number(event.target.value))}><option value={1}>Reminder 1</option><option value={2}>Reminder 2</option><option value={3}>Reminder 3</option></select></Field><Field label="Channel"><select value={channel} onChange={(event) => setChannel(event.target.value as 'WHATSAPP' | 'SMS' | 'BOTH')}><option value="WHATSAPP">WhatsApp</option><option value="SMS">SMS</option><option value="BOTH">Both</option></select></Field></>}
        {action === 'ASSIGN' && <Field label="Assign To"><input value={assignedDisplayName} onChange={(event) => setAssignedDisplayName(event.target.value)} placeholder="RM / staff name" /></Field>}
        {action === 'CASE_STATUS' && <><Field label="Case Status"><select value={caseStatus} onChange={(event) => setCaseStatus(event.target.value)}><option value="EXEMPT">Exempt</option><option value="REFUSED">Refused</option><option value="WRONG_NUMBER">Wrong number</option><option value="ESCALATED">Escalated</option><option value="CLOSED">Closed</option></select></Field><Field label="Priority"><select value={priority} onChange={(event) => setPriority(event.target.value)}><option value="NORMAL">Normal</option><option value="HIGH">High</option><option value="URGENT">Urgent</option><option value="LOW">Low</option></select></Field></>}
        {action === 'CALLBACK' && <Field label="Callback At"><input type="datetime-local" value={callbackAt} onChange={(event) => setCallbackAt(event.target.value)} /></Field>}
        <Field label="Note / Reason"><input value={note} onChange={(event) => setNote(event.target.value)} placeholder="Reason or operation note" /></Field>
      </div>
      {!permissionAllowed && <div className="bulk-action-warning">You do not have permission for this bulk action.</div>}
      <div className="bulk-action-footer"><button className="mini-btn" disabled={!permissions.export || !selectedCount} onClick={onExport}>Export Selected</button><button className="mini-btn pink" disabled={!canSubmit || submitting} onClick={() => void submit()}>{submitting ? 'Running...' : 'Run Bulk Action'}</button></div>
      {result && <div className="bulk-result-panel"><strong>{result.succeeded} succeeded, {result.failed} failed</strong>{result.results.slice(0, 20).map((item) => <div key={`${item.appointmentId}-${item.message}`} className={item.ok ? 'bulk-result-ok' : 'bulk-result-fail'}><span>{item.appointmentId}</span><em>{item.message}</em></div>)}</div>}
    </aside>
  </div>;
}

function TrackerWorkflowActionModal({ request, onClose, onSubmit, notify }: { request: TrackerWorkflowActionRequest; onClose: () => void; onSubmit: (payload: {
  kind: TrackerWorkflowActionRequest['kind'];
  row: FeedbackTrackerRow;
  value?: string;
  note?: string;
  assignedDisplayName?: string;
  callbackAt?: string;
  caseStatus?: string;
  priority?: string;
  reason?: string;
}) => Promise<void>; notify: (message: string) => void }) {
  const caseStatusValue = request.value || 'ACTIVE';
  const isCaseStatus = request.kind === 'case-status';
  const [note, setNote] = useState(defaultWorkflowNote(request));
  const [assignedDisplayName, setAssignedDisplayName] = useState(fmt(request.row.assigned_to) === '-' ? '' : fmt(request.row.assigned_to));
  const [callbackAt, setCallbackAt] = useState(defaultCallbackAt());
  const [priority, setPriority] = useState(caseStatusValue === 'ESCALATED' || caseStatusValue === 'WRONG_NUMBER' ? 'HIGH' : 'NORMAL');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const title = workflowActionTitle(request);
  const canSubmit =
    request.kind === 'assign' ? Boolean(assignedDisplayName.trim()) :
      request.kind === 'callback' ? Boolean(callbackAt.trim()) && Boolean(note.trim()) :
        isCaseStatus ? Boolean(note.trim()) :
          Boolean(note.trim());

  const submit = async () => {
    if (!canSubmit) {
      notify(requiredWorkflowMessage(request));
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({
        kind: request.kind,
        row: request.row,
        value: request.value,
        note: note.trim(),
        assignedDisplayName: assignedDisplayName.trim(),
        callbackAt,
        caseStatus: caseStatusValue,
        priority,
        reason: note.trim(),
      });
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Tracker action failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return <div className="enterprise-drawer-backdrop reminder-modal-backdrop" onClick={onClose}>
    <aside className="enterprise-drawer tracker-action-modal" onClick={(event) => event.stopPropagation()}>
      <button className="drawer-close" onClick={onClose}>x</button>
      <p className="enterprise-eyebrow">Feedback tracker action</p>
      <h3>{title}</h3>
      <p>{workflowActionDescription(request)}</p>
      <div className="mini-metric-grid tracker-action-summary-grid">
        <div><span>Patient</span><strong>{fmt(request.row.patient_name)}</strong><em>{fmt(request.row.mrn)}</em></div>
        <div><span>Visit</span><strong>{fmt(request.row.appointment_date).slice(0, 10)}</strong><em>{fmt(request.row.appointment_type)}</em></div>
        <div><span>Center</span><strong>{fmt(request.row.center)}</strong><em>{fmt(request.row.journey)}</em></div>
        <div><span>Status</span><strong>{fmt(request.row.feedback_status).replaceAll('_', ' ')}</strong><em>{fmt(request.row.message_status).replaceAll('_', ' ')}</em></div>
      </div>

      {request.kind === 'assign' && <Field label="Assign RM / Staff"><input value={assignedDisplayName} onChange={(event) => setAssignedDisplayName(event.target.value)} placeholder="Enter RM or staff name" /></Field>}
      {request.kind === 'callback' && <Field label="Callback Date / Time"><input type="datetime-local" value={callbackAt} onChange={(event) => setCallbackAt(event.target.value)} /></Field>}
      {isCaseStatus && <div className="tracker-action-case-panel">
        <div><span>Case Status</span><strong>{caseStatusValue.replaceAll('_', ' ')}</strong></div>
        <Field label="Priority"><select value={priority} onChange={(event) => setPriority(event.target.value)}><option value="LOW">Low</option><option value="NORMAL">Normal</option><option value="HIGH">High</option><option value="URGENT">Urgent</option></select></Field>
      </div>}
      <Field label={isCaseStatus ? 'Reason' : request.kind === 'callback' ? 'Callback Note' : 'Note'}><textarea value={note} onChange={(event) => setNote(event.target.value)} placeholder={isCaseStatus ? 'Enter mandatory reason for this case status' : 'Enter action note'} /></Field>

      <div className="remote-link-actions tracker-action-footer">
        <button className="mini-btn" onClick={onClose}>Cancel</button>
        <button className={isCaseStatus && caseStatusValue === 'WRONG_NUMBER' ? 'mini-btn danger-action' : 'mini-btn pink'} disabled={!canSubmit || submitting} onClick={() => void submit()}>{submitting ? 'Saving...' : title}</button>
      </div>
    </aside>
  </div>;
}

function ManualFeedbackModal({ row, token, onClose, onCompleted, notify }: { row: FeedbackTrackerRow; token?: string; onClose: () => void; onCompleted: () => Promise<void>; notify: (message: string) => void }) {
  const [session, setSession] = useState<ManualFeedbackSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [verified, setVerified] = useState(false);
  const [otp, setOtp] = useState('');
  const [devOtp, setDevOtp] = useState<string | null>(null);
  const [npsScore, setNpsScore] = useState<number | null>(null);
  const [teamMemberId, setTeamMemberId] = useState('');
  const [openFeedback, setOpenFeedback] = useState('');
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [submitting, setSubmitting] = useState(false);

  const loadSession = useCallback(async () => {
    setLoading(true);
    try {
      setSession(await getManualFeedbackSession(token, row.appointment_id));
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Unable to load manual feedback session.');
    } finally {
      setLoading(false);
    }
  }, [notify, row.appointment_id, token]);

  useEffect(() => {
    void loadSession();
  }, [loadSession]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const sendOtp = async () => {
    setSendingOtp(true);
    try {
      const result = await sendManualFeedbackOtp(token, row.appointment_id);
      setDevOtp(result.otp || null);
      notify(`Verification code sent to ${result.phoneMasked || 'registered phone'}.`);
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Unable to send manual feedback OTP.');
    } finally {
      setSendingOtp(false);
    }
  };

  const verifyOtp = async () => {
    setVerifyingOtp(true);
    try {
      await verifyManualFeedbackOtp(token, row.appointment_id, otp);
      setVerified(true);
      notify('Staff phone verified. You can complete manual feedback now.');
    } catch (error) {
      notify(error instanceof Error ? error.message : 'OTP verification failed.');
    } finally {
      setVerifyingOtp(false);
    }
  };

  const setAnswer = (key: string, value: unknown) => {
    setAnswers((current) => ({ ...current, [key]: value }));
  };

  const submit = async () => {
    if (npsScore == null) {
      notify('Select an NPS score before submitting manual feedback.');
      return;
    }
    const selectedMember = session?.teamMembers.find((member) => String(member.id) === teamMemberId);
    const payload: ManualFeedbackSubmitPayload = {
      npsScore,
      teamMemberId: selectedMember?.id ?? null,
      teamMemberName: selectedMember?.name || '',
      openFeedback,
      ratings: answers,
      questionResponses: answers,
      language: 'English',
    };
    setSubmitting(true);
    try {
      await submitManualFeedback(token, row.appointment_id, payload);
      await onCompleted();
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Manual feedback submit failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const appointment = session?.appointment;
  const canShowForm = session && !session.completed && verified;

  return <div className="enterprise-drawer-backdrop reminder-modal-backdrop" onClick={onClose}>
    <aside className="enterprise-drawer manual-feedback-modal" onClick={(event) => event.stopPropagation()}>
      <button className="drawer-close" onClick={onClose}>x</button>
      <p className="enterprise-eyebrow">Manual feedback verification</p>
      <h3>Manual Feedback</h3>
      <p>Capture feedback only after verifying the logged-in staff user's registered phone.</p>

      {loading && <div className="reminder-loading">Loading manual feedback session...</div>}
      {!loading && session && appointment && <>
        <div className="mini-metric-grid manual-session-grid">
          <div><span>Patient</span><strong>{appointment.patientName}</strong><em>{appointment.mrn}</em></div>
          <div><span>Visit</span><strong>{appointment.appointmentDate}</strong><em>{appointment.appointmentType}</em></div>
          <div><span>Center</span><strong>{appointment.center}</strong><em>{appointment.journey}</em></div>
          <div><span>Status</span><strong>{session.completed ? 'Completed' : 'Pending'}</strong><em>{fmt(row.feedback_status).replaceAll('_', ' ')}</em></div>
        </div>

        {session.completed && session.feedback && <div className="manual-completed-panel">
          <strong>Feedback already taken</strong>
          <span>This record is read-only because feedback has already been completed.</span>
          <DrawerSection title="Completion Details" rows={[
            ['Taken At', formatTimestamp(session.feedback.takenAt)],
            ['Mode', session.feedback.mode || '-'],
            ['Feedback By', session.feedback.feedbackBy || '-'],
            ['Submitted User', session.feedback.submittedBy || '-'],
            ['Staff Member', session.feedback.staffMember || '-'],
            ['NPS', session.feedback.npsScore == null ? '-' : `${session.feedback.npsScore} / ${session.feedback.npsBucket || bucket(session.feedback.npsScore)}`],
            ['Comments', session.feedback.comments || session.feedback.openFeedback || '-'],
          ]} />
        </div>}

        {!session.completed && <div className="manual-workflow">
          <div className={`manual-step ${verified ? 'complete' : ''}`}>
            <div className="manual-step-heading">
              <strong>1. Verify staff phone</strong>
              <span>{verified ? 'Verified' : `Code will be sent to ${session.verification.phoneMasked || 'your registered phone'}`}</span>
            </div>
            {!session.verification.canSendOtp && <div className="bulk-action-warning">Your user account does not have a phone number. Add a phone number before manual feedback capture.</div>}
            <div className="manual-otp-row">
              <button className="mini-btn" disabled={!session.verification.canSendOtp || sendingOtp || verified} onClick={() => void sendOtp()}>{sendingOtp ? 'Sending...' : 'Send Code'}</button>
              <input value={otp} onChange={(event) => setOtp(event.target.value.replace(/\D/g, '').slice(0, 8))} placeholder="Enter OTP" disabled={verified} />
              <button className="mini-btn pink" disabled={!otp || verifyingOtp || verified} onClick={() => void verifyOtp()}>{verifyingOtp ? 'Verifying...' : 'Verify'}</button>
            </div>
            {devOtp && <small className="manual-dev-otp">Development OTP: {devOtp}</small>}
          </div>

          {canShowForm && <div className="manual-step">
            <div className="manual-step-heading"><strong>2. Complete feedback</strong><span>Answers will be saved as manual feedback with audit trail.</span></div>
            <Field label="Staff / RM"><select value={teamMemberId} onChange={(event) => setTeamMemberId(event.target.value)}><option value="">Select staff member</option>{session.teamMembers.map((member) => <option key={member.id} value={member.id}>{member.name}{member.designation ? ` - ${member.designation}` : ''}</option>)}</select></Field>
            <div className="manual-nps-control">
              <span>NPS Score</span>
              <div>{Array.from({ length: 11 }, (_, score) => <button key={score} className={npsScore === score ? 'active' : ''} onClick={() => setNpsScore(score)}>{score}</button>)}</div>
            </div>
            <div className="manual-question-list">
              {session.questions.map((question) => <ManualQuestionControl key={question.id} question={question} value={answers[question.id]} onChange={(value) => setAnswer(question.id, value)} />)}
            </div>
            <Field label="Open Feedback"><textarea value={openFeedback} onChange={(event) => setOpenFeedback(event.target.value)} placeholder="Type patient comments or callback notes" /></Field>
            <div className="remote-link-actions">
              <button className="mini-btn" onClick={onClose}>Cancel</button>
              <button className="mini-btn pink" disabled={submitting || npsScore == null} onClick={() => void submit()}>{submitting ? 'Submitting...' : 'Submit Manual Feedback'}</button>
            </div>
          </div>}
        </div>}
      </>}
    </aside>
  </div>;
}

function ManualQuestionControl({ question, value, onChange }: { question: ManualFeedbackSession['questions'][number]; value: unknown; onChange: (value: unknown) => void }) {
  const type = String(question.question_type || '').toLowerCase();
  const label = question.question_text || 'Question';
  if (type.includes('rating') || type.includes('scale') || type.includes('number')) {
    const numeric = Number(value);
    return <div className="manual-rating-control"><span>{label}</span><div>{[1, 2, 3, 4, 5].map((score) => <button key={score} className={numeric === score ? 'active' : ''} onClick={() => onChange(score)}>{score}</button>)}</div></div>;
  }
  if (type.includes('yes') || type.includes('bool')) {
    return <div className="manual-yesno-control"><span>{label}</span><div><button className={value === 'Yes' ? 'active' : ''} onClick={() => onChange('Yes')}>Yes</button><button className={value === 'No' ? 'active' : ''} onClick={() => onChange('No')}>No</button></div></div>;
  }
  return <Field label={label}><textarea value={String(value || '')} onChange={(event) => onChange(event.target.value)} placeholder="Enter response" /></Field>;
}

function RowActionsV2({ row, permissions, openMenu, setOpenMenu, setSelectedRow, openMessageSchedule, openRemoteLink, action }: { row: FeedbackTrackerRow; permissions: TrackerPermissions; openMenu: string | null; setOpenMenu: (id: string | null) => void; setSelectedRow: (row: FeedbackTrackerRow) => void; openMessageSchedule: (row: FeedbackTrackerRow) => void; openRemoteLink: (purpose: 'COPY' | 'QR') => void; action: (kind: TrackerRowActionKind, row: FeedbackTrackerRow, value?: string) => Promise<void> }) {
  const completed = isFeedbackTaken(row);
  const completedTitle = completed ? 'Feedback is already completed.' : undefined;
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (openMenu !== row.id) return undefined;
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setOpenMenu(null);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenMenu(null);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [openMenu, row.id, setOpenMenu]);

  const openDetails = () => {
    setSelectedRow(row);
    setOpenMenu(null);
  };

  const openSchedule = () => {
    openMessageSchedule(row);
    setOpenMenu(null);
  };

  const openLink = (purpose: 'COPY' | 'QR') => {
    openRemoteLink(purpose);
    setOpenMenu(null);
  };

  return <div className="row-actions">
    <button className="mini-btn pink" disabled={!permissions.manual} title={completedTitle} onClick={() => void action('manual', row)}>Manual</button>
    <button className="mini-btn green" disabled={!permissions.sendMessage || completed} title={completedTitle} onClick={() => openMessageSchedule(row)}>Send Message</button>
    <div className="more-wrap" ref={menuRef}>
      <button className="mini-icon-btn" aria-label={`Open actions for ${fmt(row.patient_name)}`} aria-expanded={openMenu === row.id} onClick={() => setOpenMenu(openMenu === row.id ? null : row.id)}>...</button>
      {openMenu === row.id && <div className="row-menu rich-row-menu" role="menu">
        <button role="menuitem" onClick={openDetails}>View Details</button>
        <button role="menuitem" disabled={!permissions.sendMessage || completed} title={completedTitle} onClick={openSchedule}>Reminder Schedule</button>
        <button role="menuitem" disabled={!permissions.sendMessage || completed} title={completedTitle} onClick={() => openLink('COPY')}>Copy Feedback Link</button>
        <button role="menuitem" disabled={!permissions.sendMessage || completed} title={completedTitle} onClick={() => openLink('QR')}>Generate QR Code</button>
        <button role="menuitem" disabled={!permissions.sendMessage || completed} title={completedTitle} onClick={() => void action('sms', row)}>Send SMS</button>
        <button role="menuitem" disabled={!permissions.manage} onClick={() => void action('note', row)}>Add Follow-up Note</button>
        <button role="menuitem" disabled={!permissions.callback} onClick={() => void action('callback', row)}>Schedule Callback</button>
        <button role="menuitem" disabled={!permissions.assign} onClick={() => void action('assign', row)}>Transfer RM</button>
        <button role="menuitem" disabled={!permissions.escalate} onClick={() => void action('case-status', row, 'CLOSED')}>Close Case</button>
        <button role="menuitem" disabled={!permissions.escalate} onClick={() => void action('case-status', row, 'EXEMPT')}>Mark Exempt</button>
        <button role="menuitem" disabled={!permissions.escalate} onClick={() => void action('case-status', row, 'REFUSED')}>Mark Refused</button>
        <button role="menuitem" disabled={!permissions.escalate} onClick={() => void action('case-status', row, 'ESCALATED')}>Escalate Case</button>
        <button role="menuitem" disabled={!permissions.escalate} className="danger" onClick={() => void action('case-status', row, 'WRONG_NUMBER')}>Mark Wrong Mobile</button>
      </div>}
    </div>
  </div>;
}

function ReminderScheduleModal({ row, token, onClose, onSend, notify }: { row: FeedbackTrackerRow; token?: string; onClose: () => void; onSend: (attempt: number) => Promise<void>; notify: (message: string) => void }) {
  const [schedule, setSchedule] = useState<FeedbackReminderScheduleResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [sendingAttempt, setSendingAttempt] = useState<number | null>(null);

  const loadSchedule = useCallback(async () => {
    setLoading(true);
    try {
      setSchedule(await getFeedbackReminderSchedule(token, row.appointment_id));
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Unable to load reminder schedule.');
    } finally {
      setLoading(false);
    }
  }, [notify, row.appointment_id, token]);

  useEffect(() => {
    void loadSchedule();
  }, [loadSchedule]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const sendAttempt = async (item: FeedbackReminderScheduleRow) => {
    if (!item.action.allowed) return;
    setSendingAttempt(item.attempt);
    try {
      await onSend(item.attempt);
      await loadSchedule();
    } finally {
      setSendingAttempt(null);
    }
  };

  return (
    <div className="enterprise-drawer-backdrop reminder-modal-backdrop" onClick={onClose}>
      <aside className="enterprise-drawer reminder-schedule-modal" onClick={(event) => event.stopPropagation()}>
        <button className="drawer-close" onClick={onClose}>x</button>
        <p className="enterprise-eyebrow">Feedback reminder workflow</p>
        <h3>Send Message</h3>
        <p>{fmt(row.patient_name)} / {fmt(row.mrn)} / {fmt(row.phone)}</p>
        <div className="mini-metric-grid reminder-summary-grid">
          <div><span>Visit</span><strong>{fmt(row.appointment_date).slice(0, 10)}</strong><em>{fmt(row.appointment_type)}</em></div>
          <div><span>Center</span><strong>{fmt(row.center)}</strong><em>{fmt(row.doctor_name)}</em></div>
          <div><span>Feedback</span><strong>{fmt(row.feedback_status).replaceAll('_', ' ')}</strong><em>{fmt(row.message_status).replaceAll('_', ' ')}</em></div>
          <div><span>Policy</span><strong>{schedule ? schedule.workflow.manualPolicy.replaceAll('_', ' ') : '-'}</strong><em>{schedule?.workflow.isEnabled ? 'Workflow enabled' : 'Workflow disabled'}</em></div>
        </div>
        {loading && <div className="reminder-loading">Loading reminder schedule...</div>}
        {!loading && schedule && (
          <>
            <div className="reminder-policy-strip">
              <strong>{schedule.workflow.name}</strong>
              <span>Next due: {schedule.nextDueAttempt ? `Reminder ${schedule.nextDueAttempt}` : 'No reminder due'}</span>
              <span>Case: {schedule.caseStatus.replaceAll('_', ' ')}</span>
              <span>Link expiry: {schedule.workflow.linkExpiry.value} {schedule.workflow.linkExpiry.unit.toLowerCase()}</span>
            </div>
            <div className="reminder-schedule-list">
              {schedule.schedule.map((item) => (
                <div key={item.stepKey} className={`reminder-schedule-row reminder-${item.status.toLowerCase()}`}>
                  <div className="reminder-step-marker"><strong>{item.attempt}</strong></div>
                  <div className="reminder-step-body">
                    <div className="reminder-step-title">
                      <strong>{item.label}</strong>
                      <span className={`status-chip ${statusClass(item.status)}`}>{item.status.replaceAll('_', ' ')}</span>
                    </div>
                    <div className="reminder-step-meta">
                      <span>Send: {formatTimestamp(item.scheduledAt)}</span>
                      <span>Expires: {formatTimestamp(item.expiresAt)}</span>
                      <span>{item.channel}</span>
                    </div>
                    <div className="reminder-template-line">
                      {fmt(item.templateName || item.templateSettingKey)} / Token {item.tokenStatus}
                      {item.message && <> / Sent by {senderLabel(item.message.sentBy)}</>}
                    </div>
                    <small>{item.action.reason}</small>
                  </div>
                  <div className="reminder-step-action">
                    <button className="mini-btn green" disabled={!item.action.allowed || sendingAttempt === item.attempt} onClick={() => void sendAttempt(item)}>
                      {sendingAttempt === item.attempt ? 'Sending...' : item.action.kind === 'RETRY' ? 'Retry' : 'Send'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </aside>
    </div>
  );
}

function TrackerAnalytics({ rows }: { rows: FeedbackTrackerRow[] }) {
  const byCenter = [...rows.reduce((map, row) => map.set(row.center, (map.get(row.center) || 0) + 1), new Map<string, number>()).entries()].sort((a, b) => b[1] - a[1]);
  const byDoctor = [...rows.reduce((map, row) => map.set(fmt(row.doctor_name), (map.get(fmt(row.doctor_name)) || 0) + 1), new Map<string, number>()).entries()].sort((a, b) => b[1] - a[1]).slice(0, 6);
  const max = Math.max(1, ...byCenter.map(([, count]) => count));
  const promoters = rows.filter((r) => bucket(r.nps_score) === 'Promoter').length;
  const passives = rows.filter((r) => bucket(r.nps_score) === 'Passive').length;
  const detractors = rows.filter((r) => bucket(r.nps_score) === 'Detractor').length;
  const hourlyCounts = rows.reduce<number[]>((counts, row) => {
    const hour = hourOf(row.feedback_created_at || row.message_sent_at || row.appointment_date);
    if (hour != null) counts[hour] += 1;
    return counts;
  }, Array.from({ length: 24 }, () => 0));
  const hourlyMax = Math.max(1, ...hourlyCounts);
  return <div className="ops-grid-two tracker-analytics-grid">
    <div className="card enterprise-panel"><div className="enterprise-card-heading"><div><strong>Center Capture Distribution</strong><span>Visit volume by center for selected date</span></div></div><div className="country-bars">{byCenter.map(([center, count]) => <div key={center}><span>{center}</span><div><em style={{ width: `${(count / max) * 100}%` }} /></div><strong>{count}</strong></div>)}</div></div>
    <div className="card enterprise-panel"><div className="enterprise-card-heading"><div><strong>Patient Type Follow-up Load</strong><span>Visits requiring attention by patient type</span></div></div><div className="mini-metric-grid">{byDoctor.map(([doctor, count]) => <div key={doctor}><span>{doctor}</span><strong>{count} visits</strong><em>{pct(count, rows.length)}</em></div>)}</div></div>
    <div className="card enterprise-panel"><div className="enterprise-card-heading"><div><strong>Hourly Response Trend</strong><span>Tablet, remote and message activity by hour</span></div></div><div className="trend-mock">{hourlyCounts.map((count, i) => <span key={i} title={`${i}:00 - ${count} records`} style={{ height: `${count ? 18 + ((count / hourlyMax) * 72) : 8}px` }} />)}</div></div>
    <div className="card enterprise-panel"><div className="enterprise-card-heading"><div><strong>Reminder Funnel</strong><span>Message sent, delivered, opened and completed funnel</span></div></div><div className="mini-metric-grid"><div><span>Sent</span><strong>{rows.filter((r) => r.message_status !== 'NOT_SENT').length}</strong><em>WhatsApp/SMS</em></div><div><span>Delivered</span><strong>{rows.filter((r) => r.message_status === 'DELIVERED').length}</strong><em>Provider callback</em></div><div><span>Opened</span><strong>{rows.filter((r) => r.message_status === 'OPENED').length}</strong><em>Remote link opened</em></div><div><span>Completed</span><strong>{rows.filter((r) => r.feedback_status === 'REMOTE_COMPLETED').length}</strong><em>Remote feedback</em></div></div></div>
    <div className="card enterprise-panel"><div className="enterprise-card-heading"><div><strong>NPS Distribution</strong><span>Promoter, passive and detractor split</span></div></div><div className="mini-metric-grid"><div><span>Promoters</span><strong>{promoters}</strong><em>{pct(promoters, rows.length)}</em></div><div><span>Passives</span><strong>{passives}</strong><em>{pct(passives, rows.length)}</em></div><div><span>Detractors</span><strong>{detractors}</strong><em>{pct(detractors, rows.length)}</em></div></div></div>
    <div className="card enterprise-panel"><div className="enterprise-card-heading"><div><strong>Completion Channel Mix</strong><span>Tablet, remote and manual contribution</span></div></div><div className="mini-metric-grid"><div><span>Tablet</span><strong>{rows.filter((r) => String(r.feedback_mode).includes('TABLET')).length}</strong><em>Clinic capture</em></div><div><span>Remote</span><strong>{rows.filter((r) => r.feedback_status === 'REMOTE_COMPLETED').length}</strong><em>Mobile link</em></div><div><span>Manual</span><strong>{rows.filter((r) => String(r.feedback_mode).includes('MANUAL')).length}</strong><em>RM callback</em></div></div></div>
  </div>;
}

function RemoteLinkModal({ row, purpose, token, onClose, notify }: { row: FeedbackTrackerRow; purpose: 'COPY' | 'QR'; token?: string; onClose: () => void; notify: (message: string) => void }) {
  const [link, setLink] = useState<FeedbackRemoteLinkResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [copying, setCopying] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const isQr = purpose === 'QR';

  const copyUrl = useCallback(async () => {
    if (!link?.remoteUrl) return;
    setCopying(true);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link.remoteUrl);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = link.remoteUrl;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      notify('Feedback link copied.');
    } catch {
      notify('Unable to copy automatically. Select and copy the URL manually.');
    } finally {
      setCopying(false);
    }
  }, [link?.remoteUrl, notify]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const requestKey = `${token || 'cookie'}:${row.appointment_id}:${purpose}`;
    let request = remoteLinkRequests.get(requestKey);
    if (!request) {
      request = createFeedbackRemoteLink(token, row.appointment_id, purpose);
      remoteLinkRequests.set(requestKey, request);
      request.then(
        () => remoteLinkRequests.delete(requestKey),
        () => remoteLinkRequests.delete(requestKey),
      );
    }
    request
      .then((response) => {
        if (active) setLink(response);
      })
      .catch((error) => notify(error instanceof Error ? error.message : 'Unable to generate feedback link.'))
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [notify, purpose, row.appointment_id, token]);

  useEffect(() => {
    if (!isQr || !link?.remoteUrl || !canvasRef.current) return;
    const canvas = canvasRef.current;
    let cancelled = false;
    QRCode.toCanvas(canvas, link.remoteUrl, {
      errorCorrectionLevel: 'H',
      margin: 2,
      scale: 8,
      color: { dark: '#111827', light: '#ffffff' },
    }).then(() => {
      if (cancelled) return;
      const context = canvas.getContext('2d');
      if (!context) return;
      const image = new Image();
      image.onload = () => {
        if (cancelled) return;
        const size = Math.round(canvas.width * 0.18);
        const x = Math.round((canvas.width - size) / 2);
        const y = Math.round((canvas.height - size) / 2);
        const padding = 8;
        context.save();
        context.fillStyle = '#ffffff';
        roundedRect(context, x - padding, y - padding, size + padding * 2, size + padding * 2, Math.round(size * 0.22));
        context.fill();
        context.drawImage(image, x, y, size, size);
        context.restore();
      };
      image.src = '/logo.png';
    }).catch(() => notify('Unable to render QR code.'));
    return () => {
      cancelled = true;
    };
  }, [isQr, link?.remoteUrl, notify]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const downloadQr = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const anchor = document.createElement('a');
    anchor.download = `ferty9-feedback-${row.appointment_id || row.mrn}.png`;
    anchor.href = canvas.toDataURL('image/png');
    anchor.click();
  };

  return (
    <div className="enterprise-drawer-backdrop reminder-modal-backdrop" onClick={onClose}>
      <aside className="enterprise-drawer remote-link-modal" onClick={(event) => event.stopPropagation()}>
        <button className="drawer-close" onClick={onClose}>x</button>
        <p className="enterprise-eyebrow">Remote feedback {isQr ? 'QR code' : 'secure link'}</p>
        <h3>{isQr ? 'Generate QR Code' : 'Copy Feedback Link'}</h3>
        <p>{fmt(row.patient_name)} / {fmt(row.mrn)} / {fmt(row.phone)}</p>
        {loading && <div className="reminder-loading">Generating secure feedback token...</div>}
        {!loading && link && (
          <div className="remote-link-panel">
            <div className="remote-link-meta">
              <RemoteLinkInfo label="Token Status" value={link.tokenStatus} />
              <RemoteLinkInfo label="Expires" value={link.accessBound ? 'Access-bound' : formatTimestamp(link.expiresAt)} />
              <RemoteLinkInfo label="Generated" value={formatTimestamp(link.generatedAt)} />
              <RemoteLinkInfo label="Reference" value={link.tokenReference} />
            </div>
            <label className="enterprise-field remote-link-url">
              <span>Feedback URL</span>
              <input value={link.remoteUrl} readOnly onFocus={(event) => event.currentTarget.select()} />
            </label>
            {isQr && (
              <div className="remote-qr-wrap">
                <canvas ref={canvasRef} aria-label="Remote feedback QR code" />
                <small>QR contains the same secure feedback URL. The raw token is not shown in audit or history.</small>
              </div>
            )}
            <div className="remote-link-actions">
              <button className="mini-btn green" onClick={() => void copyUrl()} disabled={copying}>{copying ? 'Copying...' : 'Copy URL'}</button>
              {isQr && <button className="mini-btn" onClick={downloadQr}>Download PNG</button>}
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}

function RemoteLinkInfo({ label, value }: { label: string; value: string }) {
  return <div><span>{label}</span><strong>{value}</strong></div>;
}

function roundedRect(context: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number) {
  context.beginPath();
  context.moveTo(x + radius, y);
  context.lineTo(x + width - radius, y);
  context.quadraticCurveTo(x + width, y, x + width, y + radius);
  context.lineTo(x + width, y + height - radius);
  context.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  context.lineTo(x + radius, y + height);
  context.quadraticCurveTo(x, y + height, x, y + height - radius);
  context.lineTo(x, y + radius);
  context.quadraticCurveTo(x, y, x + radius, y);
  context.closePath();
}

function DetailsDrawer({ row, token, onClose, notify }: { row: FeedbackTrackerRow; token?: string; onClose: () => void; notify: (message: string) => void }) {
  const [timeline, setTimeline] = useState<FeedbackTrackerTimelineResponse | null>(null);
  const [detail, setDetail] = useState<FeedbackTrackerDetailResponse | null>(null);
  const [communication, setCommunication] = useState<FeedbackTrackerCommunicationHistoryResponse | null>(null);
  const [audit, setAudit] = useState<FeedbackTrackerAuditResponse | null>(null);
  const [feedback, setFeedback] = useState<FeedbackTrackerFeedbackResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([
      getFeedbackTrackerTimeline(token, row.appointment_id),
      getFeedbackTrackerDetail(token, row.appointment_id),
      getFeedbackTrackerCommunicationHistory(token, row.appointment_id),
      getFeedbackTrackerAudit(token, row.appointment_id),
      getFeedbackTrackerFeedback(token, row.appointment_id),
    ])
      .then(([timelineResult, detailResult, communicationResult, auditResult, feedbackResult]) => {
        if (!active) return;
        setTimeline(timelineResult);
        setDetail(detailResult);
        setCommunication(communicationResult);
        setAudit(auditResult);
        setFeedback(feedbackResult);
      })
      .catch((error) => notify(error instanceof Error ? error.message : 'Unable to load tracker details.'))
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [notify, row.appointment_id, token]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const appointment = detail?.appointment || {};
  const caseStatus = detail?.latestCaseStatus as Record<string, unknown> | null | undefined;
  const assignment = detail?.activeAssignment as Record<string, unknown> | null | undefined;
  const callback = detail?.nextCallback as Record<string, unknown> | null | undefined;
  const events = timeline?.events || [];
  const messages = communication?.messages || [];
  const audits = audit?.audit || [];
  const linkAudits = audit?.linkAudit || [];
  const feedbackRows = feedback?.feedback || [];

  return (
    <div className="enterprise-drawer-backdrop" onClick={onClose}>
      <aside className="enterprise-drawer" onClick={(event) => event.stopPropagation()}>
        <button className="drawer-close" onClick={onClose}>x</button>
        <p className="enterprise-eyebrow">Patient visit details</p>
        <h3>{fmt(row.patient_name)}</h3>
        <p>{fmt(row.mrn)} / {fmt(row.phone)} / {fmt(row.center)}</p>
        {loading && <div className="reminder-loading">Loading tracker details...</div>}
        <div className="mini-metric-grid">
          <div><span>Appointment</span><strong>{fmt(row.appointment_id)}</strong><em>{fmt(row.appointment_type)}</em></div>
          <div><span>Patient Type</span><strong>{fmt(row.doctor_name)}</strong><em>{fmt(row.journey)}</em></div>
          <div><span>NPS</span><strong>{fmt(row.nps_score)}</strong><em>{fmt(row.nps_bucket || bucket(row.nps_score))}</em></div>
          <div><span>Case</span><strong>{fmt(caseStatus?.case_status || 'ACTIVE')}</strong><em>{fmt(caseStatus?.priority || 'NORMAL')}</em></div>
        </div>
        <DrawerSection title="Patient Details" rows={[['Phone', appointment.phoneMasked || row.phone], ['Center', appointment.center || row.center], ['Visit', fmt(appointment.appointmentDate || row.appointment_date).slice(0, 10)], ['Status', appointment.appointmentStatus || row.appointment_status], ['Assignment', assignment?.assigned_display_name || row.assigned_to], ['Next Callback', callback?.callback_at ? formatTimestamp(callback.callback_at) : '-']]} />
        <DrawerSection title="Feedback View" rows={feedbackRows.length ? feedbackRows.map((item, index) => [`Feedback ${index + 1}`, `NPS ${fmt(item.nps_score)} / ${fmt(item.status)} / ${formatTimestamp(item.created_at)}`, fmt(item.open_feedback || item.comments || item.submitted_username)]) : [['Feedback', 'No feedback submitted yet.']]} />
        <DrawerSection title="Communication History" rows={messages.length ? messages.map((item) => [`Reminder ${fmt(item.reminder_count)} ${fmt(item.channel)}`, `${fmt(item.delivery_status || item.status)} / ${formatTimestamp(item.created_at)}`, fmt(item.failure_reason || item.template_name || item.masked_recipient)]) : [['Messages', 'No message history yet.']]} />
        <DrawerSection title="Remote Link Audit" rows={linkAudits.length ? linkAudits.map((item) => linkAuditRow(item)) : [['Remote Links', 'No remote link audit rows found.']]} />
        <div className="timeline-list">
          <h4>Timeline</h4>
          {events.length ? events.map((event) => <div key={event.id}><span /> <strong>{event.eventLabel}</strong><em>{fmt(event.eventStatus)} / {formatTimestamp(event.createdAt)}</em><small>{fmt(event.note || event.actorUsername)}</small></div>) : <small>No timeline events yet.</small>}
        </div>
        <DrawerSection title="Audit Trail" rows={audits.length ? audits.map((item) => [fmt(item.action), `${fmt(item.outcome)} / ${formatTimestamp(item.created_at)}`, fmt(item.actor)]) : [['Audit', 'No audit rows found for this appointment.']]} />
      </aside>
    </div>
  );
}

function DrawerSection({ title, rows }: { title: string; rows: Array<[unknown, unknown, unknown?]> }) {
  return <div className="tracker-drawer-section"><h4>{title}</h4>{rows.map((item, index) => <div key={`${title}-${index}`} className="tracker-drawer-row"><strong>{fmt(item[0])}</strong><span>{fmt(item[1])}</span>{item[2] ? <small>{fmt(item[2])}</small> : null}</div>)}</div>;
}

function linkAuditRow(item: Record<string, unknown>): [unknown, unknown, unknown] {
  const device = item.device_json && typeof item.device_json === 'object' ? item.device_json as Record<string, unknown> : {};
  const location = item.location_json && typeof item.location_json === 'object' ? item.location_json as Record<string, unknown> : {};
  const deviceText = [device.browser, device.userAgentFamily, device.isMobile ? 'Mobile' : null].filter(Boolean).join(' / ') || 'Device not captured';
  const locationText = location.permission ? `Location: ${fmt(location.permission)}` : 'Location: not captured';
  const status = item.token_status || item.event_status;
  const opened = item.token_opened_at ? `Opened ${formatTimestamp(item.token_opened_at)}` : 'Not opened';
  const used = item.token_used_at ? ` / Used ${formatTimestamp(item.token_used_at)}` : '';
  return [
    `${fmt(item.purpose)} / ${fmt(status)}`,
    `${fmt(item.event_label)} / ${formatTimestamp(item.created_at)}`,
    `Ref ${fmt(item.token_reference)} / ${opened}${used} / IP ${fmt(item.ip_address)} / ${deviceText} / ${locationText}`,
  ];
}

function Kpi({ label, value, tone }: { label: string; value: ReactNode; tone: string; icon: string }) {
  const displayValue = label === 'Capture Rate' && typeof value === 'number' ? `${value}%` : value;
  const marker = label.split(/[\s/]+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join('');
  const definitions: Record<string, string> = {
    'Total Visits': 'All active operational appointment visits in the tracker scope.',
    'Eligible Visits': 'Checked-out active visits eligible for feedback.',
    'Eligible Matrix': 'Checked-out eligible visits split as New Consultation / OPU / ET.',
    'Feedback Taken': 'Filtered visits with tablet, manual, or remote feedback completed.',
    'Taken at Clinic': 'Filtered completed feedback rows captured through tablet/clinic mode.',
    'Staff Acquired': 'Filtered feedback rows completed manually by staff/RM.',
    Pending: 'Checked-out eligible visits where feedback is still pending.',
    'Capture Rate': 'Feedback taken divided by eligible checked-out visits.',
    'Message Sent': 'Filtered visits with any message lifecycle record.',
    'Delivered / Opened': 'Filtered message rows marked delivered and opened.',
    'Manual / Callback': 'Filtered pending visits older than the manual follow-up threshold.',
    'High Priority': 'Filtered rows marked high priority, aged over 12 hours, or detractor.',
    'Remote Done': 'Filtered feedback completed remotely from patient mobile.',
    'Avg NPS': 'Average NPS score across filtered scored feedback rows.',
  };
  return <div className={`ops-kpi-card tone-${tone}`} title={definitions[label]}><div className="ops-kpi-icon">{marker}</div><div><strong>{displayValue}</strong><span>{label}</span></div></div>;
}

function EligibleMatrix({ matrix }: { matrix: FeedbackTrackerStats['eligibleMatrix'] }) {
  const value = matrix || { newConsultation: 0, opu: 0, et: 0 };
  return <span className="eligible-matrix-value">
    <span className="eligible-matrix-new">{value.newConsultation}</span>
    <em>/</em>
    <span className="eligible-matrix-opu">{value.opu}</span>
    <em>/</em>
    <span className="eligible-matrix-et">{value.et}</span>
  </span>;
}
function Field({ label, children }: { label: string; children: ReactNode }) {
  return <label className="enterprise-field"><span>{label}</span>{children}</label>;
}
function Insight({ label, value }: { label: string; value: string }) {
  return <div><strong>{label}</strong><span>{value}</span></div>;
}
